import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import { ExperienceTrackerContext } from '@atlassian/experience-tracker/component/ExperienceTrackerContext';
import type { ExperienceTrackerAPI } from '@atlassian/experience-tracker/ExperienceTracker';

import { useAIMateExperienceTracker } from './main';
import {
	completeRemixInsertRenderVerification,
	failRemixInsertRenderVerification,
} from './remix-insert-render-experience';
import {
	beginRemixEmbedRender,
	completeRemixEmbedRender,
	failRemixEmbedRender,
	releaseRemixEmbedRender,
	retireRemixViewPageSession,
} from './remix-view-experience';

/** Outer bound for one Remix image or infographic to display. */
export const REMIX_MEDIA_RENDER_TIMEOUT_MS = 35_000;

export type MediaRenderEvent = {
	dataConsumerSource?: string;
	mediaId?: string;
	mediaInstance: object;
} & (
	| { type: 'mounted' | 'unmounted' }
	| { renderedMediaId?: string; type: 'preview-rendered' }
	| { reason: string; type: 'error' }
);

type RemixMediaMetadata = {
	remixSubtype?: string;
	remixType?: 'image' | 'infographic';
};

type MediaTrackingLifecycle = {
	contentId?: string;
	enabled: boolean;
	insertEnabled: boolean;
	experienceTracker: ExperienceTrackerAPI;
	pageMode: 'edit' | 'live' | 'view';
	pageSessionKey?: object;
};

type PendingRenderOutcome =
	| {
			lifecycle: MediaTrackingLifecycle;
			mediaId?: string;
			type: 'success';
			renderedMediaId?: string;
	  }
	| { lifecycle: MediaTrackingLifecycle; mediaId?: string; type: 'failure'; reason: string };

type TrackedMedia = RemixMediaMetadata & {
	insertSettled: boolean;
	lifecycle: MediaTrackingLifecycle;
	mediaId: string;
	registrationId: string;
	settled: boolean;
	loadPending: boolean;
	timeout?: ReturnType<typeof setTimeout>;
};

/** Parses the persistent ADF dataConsumer marker written for Remix-generated media. */
const parseRemixMediaSource = (source?: string): RemixMediaMetadata | undefined => {
	if (source === 'remix') {
		return {};
	}
	if (!source) {
		return undefined;
	}

	const [prefix, remixType, remixSubtype, ...rest] = source.split(':');
	if (
		prefix !== 'remix' ||
		(remixType !== 'image' && remixType !== 'infographic') ||
		!remixSubtype ||
		rest.length > 0
	) {
		return undefined;
	}

	return { remixSubtype, remixType };
};

/**
 * Creates the product-owned callback passed to Atlaskit's renderer and media
 * plugin. Atlaskit reports generic media lifecycle events; this controller
 * selects persisted Remix markers for the shared page View experience and
 * matching Insert verification.
 */
export const useRemixViewMediaExperience = ({
	contentId,
	enabled,
	insertEnabled = false,
	pageMode,
}: {
	contentId?: string;
	enabled: boolean;
	insertEnabled?: boolean;
	pageMode: 'edit' | 'live' | 'view';
}): { onMediaRenderEvent?: (event: MediaRenderEvent) => void } => {
	const experienceTracker = useAIMateExperienceTracker();
	const pageSessionKey = useContext(ExperienceTrackerContext);
	const trackedMediaRef = useRef(new Map<object, TrackedMedia>());
	const pendingOutcomesRef = useRef(new Map<object, PendingRenderOutcome>());
	const retiredMediaRef = useRef(new WeakMap<object, string>());
	const nextRegistrationIdRef = useRef(0);
	const lifecycle = useMemo<MediaTrackingLifecycle>(
		() => ({ contentId, enabled, insertEnabled, experienceTracker, pageMode, pageSessionKey }),
		[contentId, enabled, insertEnabled, experienceTracker, pageMode, pageSessionKey],
	);
	const lifecycleRef = useRef(lifecycle);
	lifecycleRef.current = lifecycle;

	const release = useCallback(
		(mediaInstance: object, expectedLifecycle?: MediaTrackingLifecycle) => {
			const pendingOutcome = pendingOutcomesRef.current.get(mediaInstance);
			if (!expectedLifecycle || pendingOutcome?.lifecycle === expectedLifecycle) {
				pendingOutcomesRef.current.delete(mediaInstance);
			}
			const trackedMedia = trackedMediaRef.current.get(mediaInstance);
			if (!trackedMedia || (expectedLifecycle && trackedMedia.lifecycle !== expectedLifecycle)) {
				return;
			}

			clearTimeout(trackedMedia.timeout);
			trackedMediaRef.current.delete(mediaInstance);
			if (trackedMedia.lifecycle.enabled) {
				releaseRemixEmbedRender({
					embedId: trackedMedia.registrationId,
					experienceTracker: trackedMedia.lifecycle.experienceTracker,
					pageSessionId: trackedMedia.lifecycle.contentId,
					pageSessionKey: trackedMedia.lifecycle.pageSessionKey,
				});
			}
		},
		[],
	);

	const succeed = useCallback(
		(mediaInstance: object, renderedMediaId?: string, mediaId?: string) => {
			const trackedMedia = trackedMediaRef.current.get(mediaInstance);
			if (!trackedMedia) {
				const retiredMediaId = retiredMediaRef.current.get(mediaInstance);
				if (retiredMediaId && (!mediaId || retiredMediaId === mediaId)) {
					return;
				}
				pendingOutcomesRef.current.set(mediaInstance, {
					lifecycle: lifecycleRef.current,
					mediaId,
					renderedMediaId,
					type: 'success',
				});
				return;
			}
			if (
				(trackedMedia.settled && !trackedMedia.loadPending) ||
				(mediaId !== undefined && mediaId !== trackedMedia.mediaId) ||
				(renderedMediaId !== undefined && renderedMediaId !== trackedMedia.mediaId)
			) {
				return;
			}

			trackedMedia.settled = true;
			trackedMedia.loadPending = false;
			clearTimeout(trackedMedia.timeout);
			if (trackedMedia.lifecycle.insertEnabled && !trackedMedia.insertSettled) {
				trackedMedia.insertSettled = true;
				completeRemixInsertRenderVerification({
					type: 'mediaId',
					value: trackedMedia.mediaId,
				});
			}
			if (trackedMedia.lifecycle.enabled) {
				completeRemixEmbedRender({
					embedId: trackedMedia.registrationId,
					experienceTracker: trackedMedia.lifecycle.experienceTracker,
					pageSessionId: trackedMedia.lifecycle.contentId,
					pageSessionKey: trackedMedia.lifecycle.pageSessionKey,
				});
			}
		},
		[],
	);

	const fail = useCallback(
		(mediaInstance: object, reason: string, isTimeout = false, mediaId?: string) => {
			const trackedMedia = trackedMediaRef.current.get(mediaInstance);
			if (!trackedMedia) {
				const retiredMediaId = retiredMediaRef.current.get(mediaInstance);
				if (retiredMediaId && (!mediaId || retiredMediaId === mediaId)) {
					return;
				}
				pendingOutcomesRef.current.set(mediaInstance, {
					lifecycle: lifecycleRef.current,
					mediaId,
					reason,
					type: 'failure',
				});
				return;
			}
			if (
				(trackedMedia.settled && !trackedMedia.loadPending) ||
				(mediaId !== undefined && mediaId !== trackedMedia.mediaId)
			) {
				return;
			}

			trackedMedia.settled = true;
			if (!isTimeout) {
				trackedMedia.loadPending = false;
			}
			clearTimeout(trackedMedia.timeout);
			if (!isTimeout && trackedMedia.lifecycle.insertEnabled && !trackedMedia.insertSettled) {
				trackedMedia.insertSettled = true;
				failRemixInsertRenderVerification({
					error: new Error(reason),
					identity: { type: 'mediaId', value: trackedMedia.mediaId },
					reason: 'media_render_error',
				});
			}
			const errorType = isTimeout ? 'render_timeout' : 'media_render_error';
			if (trackedMedia.lifecycle.enabled) {
				failRemixEmbedRender({
					attributes: {
						failedRemixSubtype: trackedMedia.remixSubtype,
						failedRemixType: trackedMedia.remixType,
						renderFailureReason: errorType,
					},
					embedId: trackedMedia.registrationId,
					error: new Error(reason),
					errorType,
					experienceTracker: trackedMedia.lifecycle.experienceTracker,
					pageSessionId: trackedMedia.lifecycle.contentId,
					pageSessionKey: trackedMedia.lifecycle.pageSessionKey,
				});
			}
		},
		[],
	);

	const trackMediaRenderEvent = useCallback(
		(event: MediaRenderEvent) => {
			if (event.type === 'unmounted') {
				const trackedMedia = trackedMediaRef.current.get(event.mediaInstance);
				if (event.mediaId !== undefined && trackedMedia?.mediaId !== event.mediaId) {
					return;
				}
				release(event.mediaInstance);
				return;
			}
			if (event.type === 'preview-rendered') {
				succeed(event.mediaInstance, event.renderedMediaId, event.mediaId);
				return;
			}
			if (event.type === 'error') {
				fail(event.mediaInstance, event.reason, false, event.mediaId);
				return;
			}

			const currentLifecycle = lifecycleRef.current;
			const remixMetadata = parseRemixMediaSource(event.dataConsumerSource);
			if (!event.mediaId || !remixMetadata) {
				const pendingOutcome = pendingOutcomesRef.current.get(event.mediaInstance);
				if (pendingOutcome?.lifecycle === currentLifecycle) {
					pendingOutcomesRef.current.delete(event.mediaInstance);
				}
				return;
			}
			const retiredMediaId = retiredMediaRef.current.get(event.mediaInstance);
			if (retiredMediaId === event.mediaId) {
				return;
			}
			retiredMediaRef.current.delete(event.mediaInstance);
			const existingMedia = trackedMediaRef.current.get(event.mediaInstance);
			if (existingMedia) {
				if (
					existingMedia.lifecycle === currentLifecycle &&
					existingMedia.mediaId === event.mediaId
				) {
					return;
				}
				release(event.mediaInstance, existingMedia.lifecycle);
			}

			const registrationId = `media:${event.mediaId}:${nextRegistrationIdRef.current++}`;
			if (currentLifecycle.enabled) {
				beginRemixEmbedRender({
					attributes: {
						contentType:
							currentLifecycle.pageMode === 'live'
								? 'live-doc'
								: currentLifecycle.pageMode === 'edit'
									? 'classic-page-edit'
									: 'classic-page-view',
						pageMode: currentLifecycle.pageMode,
						product: 'confluence',
						remixSubtype: remixMetadata.remixSubtype,
						remixType: remixMetadata.remixType,
					},
					embedId: registrationId,
					renderIdentity: event.mediaId,
					experienceTracker: currentLifecycle.experienceTracker,
					pageSessionId: currentLifecycle.contentId,
					pageSessionKey: currentLifecycle.pageSessionKey,
				});
			}

			const timeout = currentLifecycle.enabled
				? setTimeout(() => {
						fail(event.mediaInstance, 'Remix media did not render within the expected time', true);
					}, REMIX_MEDIA_RENDER_TIMEOUT_MS)
				: undefined;
			trackedMediaRef.current.set(event.mediaInstance, {
				...remixMetadata,
				insertSettled: false,
				lifecycle: currentLifecycle,
				mediaId: event.mediaId,
				registrationId,
				settled: false,
				loadPending: true,
				timeout,
			});

			const pendingOutcome = pendingOutcomesRef.current.get(event.mediaInstance);
			if (
				pendingOutcome?.lifecycle !== currentLifecycle ||
				(pendingOutcome.mediaId !== undefined && pendingOutcome.mediaId !== event.mediaId)
			) {
				if (pendingOutcome) {
					pendingOutcomesRef.current.delete(event.mediaInstance);
				}
				return;
			}
			pendingOutcomesRef.current.delete(event.mediaInstance);
			if (pendingOutcome.type === 'success') {
				succeed(event.mediaInstance, pendingOutcome.renderedMediaId, pendingOutcome.mediaId);
			} else {
				fail(event.mediaInstance, pendingOutcome.reason, false, pendingOutcome.mediaId);
			}
		},
		[fail, release, succeed],
	);
	const onMediaRenderEvent = useCallback(
		(event: MediaRenderEvent) => {
			if (!lifecycleRef.current.enabled && !lifecycleRef.current.insertEnabled) {
				return;
			}
			try {
				trackMediaRenderEvent(event);
			} catch {
				// Reliability instrumentation must never interrupt the media rendering lifecycle.
				return;
			}
		},
		[trackMediaRenderEvent],
	);

	useEffect(() => {
		if (!enabled && !insertEnabled) {
			return;
		}
		const trackedMedia = trackedMediaRef.current;
		const pendingOutcomes = pendingOutcomesRef.current;
		const retiredMedia = retiredMediaRef.current;

		return () => {
			for (const [mediaInstance, media] of [...trackedMedia.entries()]) {
				if (media.lifecycle === lifecycle) {
					retiredMedia.set(mediaInstance, media.mediaId);
					release(mediaInstance, lifecycle);
				}
			}
			for (const [mediaInstance, pendingOutcome] of pendingOutcomes) {
				if (pendingOutcome.lifecycle === lifecycle) {
					pendingOutcomes.delete(mediaInstance);
				}
			}

			const currentLifecycle = lifecycleRef.current;
			const leftTrackedDocument =
				lifecycle.enabled &&
				(!currentLifecycle.enabled || currentLifecycle.contentId !== lifecycle.contentId);
			if (leftTrackedDocument) {
				retireRemixViewPageSession({
					experienceTracker: lifecycle.experienceTracker,
					pageSessionId: lifecycle.contentId,
					pageSessionKey: lifecycle.pageSessionKey,
				});
			}
		};
	}, [enabled, insertEnabled, lifecycle, release]);

	return { onMediaRenderEvent: enabled || insertEnabled ? onMediaRenderEvent : undefined };
};
